import { Control, useFieldArray } from 'react-hook-form';
import StyledFormSection from '../styled/StyledFormSection';
import StyledInput from '../styled/StyledInput';
import StyledButton from '../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { useTranslation } from '@/i18n';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface ContactsSectionProps {
  control: Control<HotelFormDataWithTranslations>;
  isViewMode?: boolean;
}

const ContactsSection = ({
  control,
  isViewMode = false,
}: ContactsSectionProps) => {
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts.coordinates',
  });

  const {
    fields: secondaryEmails,
    append: appendSecondaryEmail,
    remove: removeSecondaryEmail,
  } = useFieldArray({
    control,
    name: 'contacts.secondaryEmails',
  });

  return (
    <StyledFormSection sectionTitle={t('hotels.contacts')}>
      {fields.map((field, index) => (
        <div key={field.id} className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            name={`contacts.coordinates.${index}.name`}
            title={t('hotels.contact_', { index: index + 1 })}
            placeholder={t('hotels.contact_', { index: index + 1 })}
            disabled={isViewMode}
          />
          <StyledInput
            control={control}
            name={`contacts.coordinates.${index}.phoneNumber`}
            title={t('hotels.phoneNumber')}
            placeholder={t('hotels.phoneNumber')}
            type="tel"
            titleIcon={
              index > 0 && !isViewMode ? (
                <TrashIcon
                  className="w-5 h-5 stroke-gray-500 hover:stroke-brand-600 hover:cursor-pointer"
                  onClick={() => remove(index)}
                />
              ) : undefined
            }
            disabled={isViewMode}
          />
        </div>
      ))}
      {!isViewMode && (
        <StyledButton
          title={t('hotels.addPhoneNumber')}
          variant="link"
          className="text-brand-600 w-fit p-0"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
          onClick={() => append({ name: undefined, phoneNumber: undefined })}
        />
      )}
      <StyledInput
        control={control}
        name="contacts.website"
        title={t('hotels.website')}
        placeholder={t('hotels.website')}
        disabled={isViewMode}
      />
      <StyledInput
        control={control}
        name="contacts.mainEmail"
        title={t('hotels.mainEmail')}
        placeholder={t('hotels.mainEmail')}
        type="email"
        disabled={isViewMode}
      />
      <StyledInput
        control={control}
        name="contacts.reservationsEmail"
        title={t('hotels.reservationsEmail')}
        placeholder={t('hotels.reservationsEmail')}
        type="email"
        disabled={isViewMode}
      />
      <StyledInput
        control={control}
        name="contacts.stockAlertEmail"
        title={t('hotels.stockAlertEmail')}
        placeholder={t('hotels.stockAlertEmail')}
        type="email"
        disabled={isViewMode}
      />
      {secondaryEmails.map((email, index) => (
        <StyledInput
          key={email.id}
          control={control}
          name={`contacts.secondaryEmails.${index}.email`}
          title={t('hotels.secondaryEmail_', { index: index + 1 })}
          placeholder={t('hotels.secondaryEmail')}
          type="email"
          titleIcon={
            !isViewMode ? (
              <TrashIcon
                className="w-5 h-5 stroke-gray-500 hover:stroke-brand-600 hover:cursor-pointer"
                onClick={() => removeSecondaryEmail(index)}
              />
            ) : undefined
          }
          disabled={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          title={t('hotels.addEmail')}
          variant="link"
          className="text-brand-600 w-fit p-0"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
          onClick={() => appendSecondaryEmail({ email: undefined })}
        />
      )}
    </StyledFormSection>
  );
};

export default ContactsSection;
